import { Attachment } from "./types";
import { resolveEmbedThumbnailUrl } from "./media/embed";

export const defaultPosition = {
  tirage: "tirageDescriptionPhotoFace",
  artwork: "oeuvresPhotoFaceOeuvre",
  photography: "photographyPhotoFace",
  document: "documentsPhotoCouverture",
  bibliography: "bibliographieDescriptionPhotoCouverture",
  sample: "exemplairedansestampesmultiplesdescriptionphotoexemplaire",
  audiovisual: "audiovisuelDescriptionPhoto",
  multipleprint: "estampesMultiplesDescriptionPhotoFaceOeuvre",
  author: "authorPhotoPortrait",
  auction: "auctionPhotoExterieur",
  exhibition: "exhibitionPhotoExterieur",
  writing: "ecritsPhotoPremierePage",
  multiplesample: "exemplairePositionPhotoFace",
  sculptureEdition: "sculptureEditionDescriptionPhotoFace",
} as const;

export const defaultPositionValues = Object.values(defaultPosition);

export const optionsPosition = {
  /** Order matches sculpture labelling: face, back, 3/4 fronts, profiles, 3/4 backs, then views and variants. */
  sculptureEdition: [
    "sculptureEditionDescriptionPhotoFace",
    "sculptureEditionDescriptionPhotoArriere",
    "sculptureEditionDescriptionPhoto34GaucheAvant",
    "sculptureEditionDescriptionPhoto34DroiteAvant",
    "sculptureEditionDescriptionPhotoProfilGauche",
    "sculptureEditionDescriptionPhotoProfilDroit",
    "sculptureEditionDescriptionPhoto34GaucheArriere",
    "sculptureEditionDescriptionPhoto34DroiteArriere",
    "sculptureEditionDescriptionPhotoDessus",
    "sculptureEditionDescriptionPhotoDessous",
    "sculptureEditionDescriptionPhotoIntérieur",
    "sculptureEditionDescriptionPhotoSocle",
    "sculptureEditionDescriptionPhotoDetail",
    "sculptureEditionDescriptionPhotoCaisse",
    "sculptureEditionDescriptionPhotoAutre",
    "sculptureEditionDescriptionPhotoEtat",
    "sculptureEditionDescriptionPhotoEssai",
  ],
  tirage: [
    "tirageDescriptionPhotoFace",
    "tirageDescriptionPhotoDos",
    "tirageDescriptionPhotoVariante",
    "tirageDescriptionPhotoDétail",
    "tirageDescriptionPhotoAutre",
  ],
  author: ["authorPhotoPortrait", "authorPhotoOther"],
  auction: ["auctionPhotoExterieur", "auctionPhotoAutres"],
  exhibition: ["exhibitionPhotoExterieur", "exhibitionPhotoAutres"],
  photos: ["photosPhoto", "photosPhotoDos", "photosPhotoVariante", "autres"],
  photography: [
    "photographyPhotoFace",
    "photographyPhotoDos",
    "photographyPhotoVariante",
    "photographyPhotoAutre",
  ],
  artwork: [
    "oeuvresPhotoFaceOeuvre",
    "oeuvresPhotoDosOeuvre",
    "oeuvresPhotoState",
    "oeuvresRegiePhotoCadre",
    "oeuvresRegiePhotoCaisse",
    "oeuvresPhotoAutres",
  ],
  bibliography: [
    "bibliographieDescriptionPhotoCouverture",
    "bibliographieDescriptionPhotoAutresPages",
  ],
  audiovisual: [
    "audiovisuelDescriptionPhoto",
    "audiovisuelDescriptionPhotoSupplementaire",
  ],
  sample: [
    "exemplaireDansEstampesMultiplesDescriptionPhotoExemplaire",
    "exemplaireDansEstampesMultiplesDescriptionPhotoCachetSignature",
  ],
  multipleprint: [
    "estampesMultiplesDescriptionPhotoFaceOeuvre",
    "estampesMultiplesDescriptionPhotoDosOeuvre",
    "estampesMultiplesDescriptionPhotoMatrice",
    "estampesMultiplesDescriptionPhotoCadreOeuvre",
    "estampesMultiplesDescriptionPhotoCaisseOeuvre",
    "estampesMultiplesDescriptionPhotoDocumentAnnexe",
    "estampesMultiplesDescriptionPhotoEtat",
    "estampesMultiplesDescriptionPhotoEssai",
    "estampesMultiplesDescriptionPhotoPrototype",
    "estampesMultiplesDescriptionPhotoMaquette",
  ],
  writing: ["ecritsPhotoPremierePage", "ecritsPhotoAutresPage"],
  document: ["documentsPhotoCouverture", "documentsPhotoAutresPages"],
  ensemble: [
    "ensemblesDescriptionLogoAuteur",
    "ensemblesDescriptionEnsembleImage",
  ],
  multiplesample: [
    "exemplairePositionPhotoFace",
    "exemplairePositionPhotoDosOeuvre",
    "exemplairePositionPhotoCadreOeuvre",
    "exemplairePositionPhotoCaisseOeuvre",
    "exemplairePositionPhotoDocumentAnnexe",
    "exemplairePositionPhotoEtat",
    "exemplairePositionPhotoEssai",
    "exemplairePositionPhotoPrototype",
    "exemplairePositionPhotoMaquette",
    "exemplairePositionPhotoAutre",
  ],
  collector: [],
} as const;

export const mainPositions = [
  "oeuvresPhotoFaceOeuvre",
  "estampesMultiplesDescriptionPhotoFaceOeuvre",
  "photographyPhotoFace",
  "audiovisuelDescriptionPhoto",
  "ecritsPhotoPremierePage",
  "documentsPhotoCouverture",
  "tirageDescriptionPhotoFace",
  "exemplairePositionPhotoFace",
  "sculptureEditionDescriptionPhotoFace",
];

export const isMainPosition = (position: string): boolean =>
  mainPositions.includes(position);

/** Main "face" positions on an artwork document (generic œuvre or sculpture master). */
export const ARTWORK_DOCUMENT_MAIN_FACE_POSITIONS = [
  "oeuvresPhotoFaceOeuvre",
  "sculptureEditionDescriptionPhotoFace",
] as const;

/** Main "back" positions paired with face for admin docs and similar flows. */
export const ARTWORK_DOCUMENT_MAIN_BACK_POSITIONS = [
  "oeuvresPhotoDosOeuvre",
  "sculptureEditionDescriptionPhotoArriere",
] as const;

export function isArtworkDocumentMainFacePosition(position: string): boolean {
  return (ARTWORK_DOCUMENT_MAIN_FACE_POSITIONS as readonly string[]).includes(
    position,
  );
}

/** Face + back keys excluded from secondary-attachment pickers on artwork documents. */
export const ARTWORK_DOCUMENT_PRIMARY_POSITION_EXCLUSIONS: string[] = [
  ...ARTWORK_DOCUMENT_MAIN_FACE_POSITIONS,
  ...ARTWORK_DOCUMENT_MAIN_BACK_POSITIONS,
];

/** Face / dos d'un exemplaire (certificat admin). */
export const MULTIPLESAMPLE_DOCUMENT_MAIN_FACE_POSITIONS = [
  "exemplairePositionPhotoFace",
] as const;

export const MULTIPLESAMPLE_DOCUMENT_MAIN_BACK_POSITIONS = [
  "exemplairePositionPhotoDosOeuvre",
] as const;

export const MULTIPLESAMPLE_DOCUMENT_PRIMARY_POSITION_EXCLUSIONS: string[] = [
  ...MULTIPLESAMPLE_DOCUMENT_MAIN_FACE_POSITIONS,
  ...MULTIPLESAMPLE_DOCUMENT_MAIN_BACK_POSITIONS,
];

export type PublicAttachmentFormat =
  | "mediumPublicUrl"
  | "largePublicUrl"
  | "publicUrl";

export type TypeDefaultImage = keyof typeof defaultPosition;
export type TypeOptionPosition = keyof typeof optionsPosition;

/** Maps master attachment positions to child document positions (exemplaire). */
export const POSITION_MAP_MULTIPLEPRINT_TO_SAMPLE: Record<string, string> = {
  estampesMultiplesDescriptionPhotoFaceOeuvre: "exemplairePositionPhotoFace",
  estampesMultiplesDescriptionPhotoDosOeuvre:
    "exemplairePositionPhotoDosOeuvre",
  estampesMultiplesDescriptionPhotoMatrice: "exemplairePositionPhotoAutre",
  estampesMultiplesDescriptionPhotoCadreOeuvre:
    "exemplairePositionPhotoCadreOeuvre",
  estampesMultiplesDescriptionPhotoCaisseOeuvre:
    "exemplairePositionPhotoCaisseOeuvre",
  estampesMultiplesDescriptionPhotoDocumentAnnexe:
    "exemplairePositionPhotoDocumentAnnexe",
  estampesMultiplesDescriptionPhotoEtat: "exemplairePositionPhotoEtat",
  estampesMultiplesDescriptionPhotoEssai: "exemplairePositionPhotoEssai",
  estampesMultiplesDescriptionPhotoPrototype: "exemplairePositionPhotoPrototype",
  estampesMultiplesDescriptionPhotoMaquette: "exemplairePositionPhotoMaquette",
};

/** Maps master artwork positions to tirage positions. */
export const POSITION_MAP_ARTWORK_TO_TIRAGE: Record<string, string> = {
  oeuvresPhotoFaceOeuvre: "tirageDescriptionPhotoFace",
  oeuvresPhotoDosOeuvre: "tirageDescriptionPhotoDos",
  oeuvresPhotoState: "tirageDescriptionPhotoDétail",
  oeuvresRegiePhotoCadre: "tirageDescriptionPhotoDétail",
  oeuvresRegiePhotoCaisse: "tirageDescriptionPhotoAutre",
  oeuvresPhotoAutres: "tirageDescriptionPhotoAutre",
  photographyPhotoFace: "tirageDescriptionPhotoFace",
  photographyPhotoDos: "tirageDescriptionPhotoDos",
  photographyPhotoVariante: "tirageDescriptionPhotoVariante",
  photographyPhotoAutre: "tirageDescriptionPhotoAutre",
};

/** Sculpture master and edition share the same position keys. */
export const POSITION_MAP_SCULPTURE_EDITION_TO_EDITION: Record<string, string> =
  Object.fromEntries(
    optionsPosition.sculptureEdition.map((position) => [position, position]),
  );

export function mapMasterPositionToChild(
  masterPosition: string,
  map: Record<string, string>,
  fallback: string,
): string {
  return map[masterPosition] ?? fallback;
}

export type CopyFromMasterConfig = {
  masterDocId: string;
  masterPositionType: TypeOptionPosition;
  positionMap: Record<string, string>;
  fallbackPosition: string;
};

function safeParseDetails(
  details: string | null | undefined,
): Record<string, unknown> | null {
  if (!details || details === '""' || details === "") return null;
  try {
    const parsed = JSON.parse(details);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

function resolveAttachmentUrl(
  attachment: Attachment | undefined,
  preferredSize: PublicAttachmentFormat,
): string | null {
  if (!attachment) return null;
  return (
    attachment[preferredSize] ??
    attachment.publicUrl ??
    attachment.largePublicUrl ??
    attachment.mediumPublicUrl ??
    null
  );
}

export function getDefaultImage(
  images: Attachment[],
  type: TypeDefaultImage,
  size: PublicAttachmentFormat = "mediumPublicUrl",
) {
  if (images.length === 0) {
    return null;
  }

  const defaultImage = images.find((image) => {
    const parsed = safeParseDetails(image.details);
    return parsed?.defaultImage === true;
  });
  if (defaultImage) {
    return resolveAttachmentUrl(defaultImage, size);
  }

  const imageDefaultPosition = images.find(
    (image) => image.position === defaultPosition[type],
  );

  if (imageDefaultPosition) {
    return resolveAttachmentUrl(imageDefaultPosition, size);
  }

  return resolveAttachmentUrl(images[0], size);
}

export function getDefaultAttachment<T extends Attachment>(
  attachments: T[],
  type: keyof typeof defaultPosition,
): T | null {
  if (attachments.length === 0) {
    return null;
  }
  const defaultImage = attachments.find((attachment) => {
    const parsed = safeParseDetails(attachment.details);
    return parsed?.defaultImage === true;
  });
  if (defaultImage) {
    return defaultImage;
  }

  const imageDefaultPosition = attachments.find(
    (attachment) => attachment.position === defaultPosition[type],
  );

  if (imageDefaultPosition) {
    return imageDefaultPosition;
  }

  return attachments[0];
}

function sortByPublicationOrder(attachments: Attachment[]): Attachment[] {
  return attachments
    .map((attachment, index) => ({ attachment, index }))
    .sort((a, b) => {
      const byOrder = (a.attachment.order ?? 0) - (b.attachment.order ?? 0);
      return byOrder !== 0 ? byOrder : a.index - b.index;
    })
    .map(({ attachment }) => attachment);
}

/**
 * Choix pour vignette/fullscreen : face, puis première vraie photo dans l'ordre
 * de publication, puis image générique d'instance.
 */
export function selectAttachmentForPreview(
  attachments: Attachment[],
  type: TypeDefaultImage,
): Attachment | null {
  if (attachments.length === 0) {
    return null;
  }
  const orderedAttachments = sortByPublicationOrder(attachments);
  const expected = defaultPosition[type];
  const atDefaultReal = orderedAttachments.find(
    (a) => a.position === expected && a.isGenericImage !== true,
  );
  if (atDefaultReal) {
    return atDefaultReal;
  }

  const firstReal = orderedAttachments.find((a) => a.isGenericImage !== true);
  if (firstReal) {
    return firstReal;
  }

  const generic = orderedAttachments.find((a) => a.isGenericImage === true);
  return generic ?? orderedAttachments[0];
}

export type GalleryImageItem = {
  id: string;
  url: string;
  largeUrl: string;
  alt: string;
  kind?: Attachment["kind"];
  videoUrl?: string | null;
  embedProvider?: string;
  embedVideoId?: string;
  embedUrl?: string;
};

export function isGalleryVideoItem(
  item: Pick<GalleryImageItem, "kind" | "videoUrl">,
): boolean {
  return item.kind === "video" && Boolean(item.videoUrl);
}

export function isGalleryEmbedItem(
  item: Pick<GalleryImageItem, "kind">,
): boolean {
  return item.kind === "embed";
}

/** Images affichables en plein écran (hors image générique), dans l'ordre de publication. */
export function attachmentsToGalleryImages(
  attachments: Attachment[] | undefined | null,
): GalleryImageItem[] {
  if (!attachments?.length) {
    return [];
  }
  return sortByPublicationOrder(attachments)
    .filter(
      (attachment) =>
        !!attachment.id &&
        attachment.isGenericImage !== true &&
        (attachment.kind === "embed" ||
          (attachment.kind === "video" && !!attachment.videoPublicUrl) ||
          !!(
            attachment.mediumPublicUrl ||
            attachment.posterPublicUrl ||
            attachment.largePublicUrl ||
            attachment.publicUrl
          )),
    )
    .map((attachment) => {
      if (attachment.kind === "embed") {
        const thumbnail =
          resolveEmbedThumbnailUrl(
            attachment.embedProvider,
            attachment.embedVideoId,
            attachment.embedUrl,
          ) ?? "";
        return {
          id: attachment.id as string,
          url: thumbnail,
          largeUrl: thumbnail,
          alt: attachment.name || "",
          kind: attachment.kind,
          videoUrl: null,
          embedProvider: attachment.embedProvider,
          embedVideoId: attachment.embedVideoId,
          embedUrl: attachment.embedUrl,
        };
      }

      const previewUrl =
        attachment.kind === "video"
          ? attachment.posterPublicUrl ||
            attachment.mediumPublicUrl ||
            attachment.publicUrl ||
            attachment.largePublicUrl ||
            ""
          : attachment.mediumPublicUrl ||
            attachment.publicUrl ||
            attachment.largePublicUrl ||
            "";
      const largeUrl =
        attachment.kind === "video"
          ? attachment.videoPublicUrl ||
            attachment.largePublicUrl ||
            previewUrl
          : attachment.largePublicUrl ||
            attachment.publicUrl ||
            previewUrl;

      return {
        id: attachment.id as string,
        url: previewUrl,
        largeUrl,
        alt: attachment.name || "",
        kind: attachment.kind,
        videoUrl:
          attachment.kind === "video" ? attachment.videoPublicUrl ?? null : null,
      };
    });
}

export function galleryIndexForAttachment(
  gallery: GalleryImageItem[],
  attachmentId: string | undefined,
): number {
  if (!attachmentId) {
    return 0;
  }
  const index = gallery.findIndex((item) => item.id === attachmentId);
  return index >= 0 ? index : 0;
}
